import { Component, DestroyRef, signal, inject, viewChild } from '@angular/core';
import {
  IWizardStepDefinition,
  localizeWithFallback,
  ModalBuilder,
  StepperSuccessModal,
  ZnConfirmation,
  ZnStepperWizard,
} from '@zendra/shared';
import { LocalizationService } from '@abp/ng.core';
import { Router } from '@angular/router';
import { StepCalculateProductivity } from './step-calculate-productivity/step-calculate-productivity';
import { StepCompleteOtherParameters } from './step-complete-other-parameters/step-complete-other-parameters';
import { StepInventoryValidation } from './step-inventory-validation/step-inventory-validation';
import { StepStandSelection } from './step-stand-selection/step-stand-selection';
import { Confirmation } from '@abp/ng.theme.shared';
import { COMPLETE_STEPPER_MESSAGE, HARVESTPREPARATIONSTANDCYCLEROTATION_CONFIG } from './stand-cycle-rotation-harvest-preparation.config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StandCycleRotationOrchestratorService } from 'orchestrator';
import { IStepDataItem } from './interfaces/IStepDataItem';
import { FinalizeHarvestPreparationItemDto } from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/stand-cycle-rotation/harvest-preparation/models';
import { isApplyHerbicideSelected } from './helpers/harvest-preparation.helpers';

@Component({
  selector: 'lib-stand-cycle-rotation-harvest-preparation',
  standalone: true,
  imports: [ZnStepperWizard, StepperSuccessModal],
  templateUrl: './stand-cycle-rotation-harvest-preparation.html',
})
export class StandCycleRotationHarvestPreparation {
  private readonly confirmationService = inject(ZnConfirmation);
  private readonly localizationService = inject(LocalizationService);
  private readonly modalBuilder = inject(ModalBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly service = inject(StandCycleRotationOrchestratorService);
  protected readonly wizard = viewChild(ZnStepperWizard);
  
  private readonly localizationKeyPrefix = HARVESTPREPARATIONSTANDCYCLEROTATION_CONFIG.localizationKey;
  protected readonly successModalVisible = signal(false);
  protected readonly successMessageKey =
    'Geographic::StandCycleRotationHarvestPreparation.StepInventoryValidation.SuccessMessage';


  protected readonly stepDefinitions: IWizardStepDefinition[] = [
    {
      id: 1,
      title: localizeWithFallback(
        this.localizationService,
        `${this.localizationKeyPrefix}.StepStandSelection.Title`,
      ),
      component: StepStandSelection,
    },
    {
      id: 2,
      title: localizeWithFallback(
        this.localizationService,
        `${this.localizationKeyPrefix}.StepCalculateProductivity.Title`,
      ),
      component: StepCalculateProductivity,
    },
    {
      id: 3,
      title: localizeWithFallback(
        this.localizationService,
        `${this.localizationKeyPrefix}.StepCompleteOtherParameters.Title`,
      ),
      component: StepCompleteOtherParameters,
    },
    {
      id: 4,
      title: localizeWithFallback(
        this.localizationService,
        `${this.localizationKeyPrefix}.StepInventoryValidation.Title`,
      ),
      component: StepInventoryValidation,
    },
  ];

  protected onSubmitWizard(data: Record<number, unknown>) {
    const modalRef = this.modalBuilder.openModal(COMPLETE_STEPPER_MESSAGE.component, COMPLETE_STEPPER_MESSAGE);

    modalRef.closed
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((confirm: boolean) => {
      if(confirm) {
        const finalData = Array.isArray(data[4]) ? (data[4] as IStepDataItem[]) : [];
        const itemsList = finalData.map(item => {
          return {
            applyFeller: item.applyFeller,
            dab: item.dab,
            isApplyHerbicideSelected: isApplyHerbicideSelected(
              item.stumpApplicationId,
              item.stumpApplicationName,
            ),
            isStumpCandidateToRegrowth: item.isStumpCandidateToRegrowth,
            isInventoryValidated: item.isInventoryValidated,
            measurementTypeId: item.measurementTypeId,
            productivityParameterId: item.productivityParameterId,
            standCycleRotationId: item.standCycleRotationId,
            stumpApplicationId: item.stumpApplicationId
        
        } as FinalizeHarvestPreparationItemDto;
        });
        this.service.finalizeHarvestPreparation({items: itemsList})
        .pipe(
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: result => {
            this.successModalVisible.set(true);
          },
          error: error => {
            console.log(error)
          }
        });
      }
    });
  }

  protected onCancel() {
    if (!this.hasPendingChanges()) {
      void this.redirectToHome();
      return;
    }

    this.confirmationService.showCancelConfirmation({
      messageKey: 'LanguageService::ConfirmCancelChangesHarvestPreparation',
      titleKey: 'LanguageService::ConfirmCancelChangesTitleHarvestPreparation',
    }).subscribe(status => {
      if (status === Confirmation.Status.confirm) {
        void this.redirectToHome();
      }
    });
  }

  protected hasPendingChanges(): boolean {
    return this.wizard()?.hasPendingChanges() ?? false;
  }

  private async redirectToHome() {
    await this.router.navigateByUrl('/');
  }

  protected async onSuccessModalVisibleChange(isVisible: boolean): Promise<void> {
    this.successModalVisible.set(isVisible);
    if (!isVisible) {
      await this.router.navigateByUrl('/geographic/stand-cycle-rotation');
    }
  }
}
